import React, { useCallback, useState } from "react";
import { Box, Link, Typography } from "@mui/joy";
import { useCommentStore, Comment } from "../../store/commentStore";
import { useAuthStore } from "../../store/authStore";
import { CommentItem } from "./CommentItem";
import { CommentInput } from "./CommentInput";

const EMPTY_COMMENTS: Comment[] = [];

interface CommentSectionProps {
  activityId: string;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  activityId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const user = useAuthStore((s) => s.user);

  const comments = useCommentStore(
    useCallback((s) => s.commentsByActivity[activityId] ?? EMPTY_COMMENTS, [activityId])
  );
  const commentCount = useCommentStore(
    useCallback((s) => s.commentCountByActivity[activityId] ?? 0, [activityId])
  );
  const loading = useCommentStore(
    useCallback((s) => s.loadingByActivity[activityId] ?? false, [activityId])
  );
  const fetchComments = useCommentStore((s) => s.fetchComments);
  const addComment = useCommentStore((s) => s.addComment);
  const deleteComment = useCommentStore((s) => s.deleteComment);

  const handleExpand = async () => {
    setExpanded(true);
    await fetchComments(activityId);
  };

  const handleAddComment = async (content: string) => {
    await addComment(activityId, content);
  };

  const handleDeleteComment = (commentId: string) => {
    deleteComment(commentId, activityId);
  };

  const showViewAll = !expanded && commentCount > 2;
  const displayedComments = expanded ? comments : comments.slice(-2);

  return (
    <Box
      data-testid={`comment-section-${activityId}`}
      onClick={(e) => e.stopPropagation()}
      sx={{
        mt: 1.5,
        pt: 1.5,
        borderTop: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {showViewAll && (
        <Link
          data-testid={`comment-view-all-${activityId}`}
          component="button"
          level="body-xs"
          sx={{
            color: "#888",
            fontWeight: 500,
            mb: 0.5,
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={handleExpand}
        >
          View all {commentCount} comments
        </Link>
      )}

      {loading && (
        <Typography level="body-xs" sx={{ color: "#aaa", py: 0.5 }}>
          Loading comments...
        </Typography>
      )}

      {displayedComments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          isOwn={user?.id === comment.user_id}
          onDelete={handleDeleteComment}
        />
      ))}

      <CommentInput
        activityId={activityId}
        onSubmit={handleAddComment}
      />
    </Box>
  );
};
